import React, { FormEvent, useState } from "react";
import { Button, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import InputMask from 'react-input-mask';
import SearchPhysicianModel from "./Models/SearchPhysicianModel";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import ReCAPTCHA from "react-google-recaptcha";
import { useEffect } from "react";
import { configService } from "../../Services/Config/ConfigService";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { ValidatorHelper } from "../Shared/ValidatorHelper";
import { getDateWithOffset } from "../Shared/Utility/GetDateWithOffset";

interface IProps {
    onSubmit: (e: FormEvent<HTMLFormElement>, model: SearchPhysicianModel) => void;
}
export function SearchPhysician(props: IProps) {
    const [model, setModel] = useState<SearchPhysicianModel>(new SearchPhysicianModel());
    const [captchaResponse, setCaptchaResponse] = useState<string>(null);
    const [siteKey, setSiteKey] = useState<string>(null);

    var ssn4Ref = React.createRef<HTMLInputElement>();

    //Get Captcha Site Key from Config
    useEffect(() => {
        configService.getConfiguration().then(configuration => {
            setSiteKey(configuration.Captcha.SiteKey);
        });
    }, [])

    //Matching Captcha Timeout
    useEffect(() => {
        let oneMinute = 60000;
        setTimeout(() => setCaptchaResponse(null), oneMinute);
    }, [captchaResponse]);

    const updateLastName = (value: string) => {
        let updated = deepCopy(model);
        updated.lastName = value;
        setModel(updated);
    }

    const updateDateOfBirth = (value: string) => {
        let updated = deepCopy(model);
        updated.dateOfBirth = value;

        if (Date.parse(value)) {
            updated.dateOfBirth = getDateWithOffset(value).toJSON();
        }

        setModel(updated);
    }

    const updateLastFourOfSSN = (value: string) => {
        ssn4Ref && ValidatorHelper.clearCustomValidation(ssn4Ref.current);
        let updated = deepCopy(model);
        updated.ssn4 = value;
        setModel(updated);
    }

    const handleCaptcha = (response: string) => {
        setCaptchaResponse(response)

        let updated = deepCopy(model);
        updated.siteToken = response;
        setModel(updated);
    }

    const onSubmit = (e: FormEvent<HTMLFormElement>, model: SearchPhysicianModel) => {
        e.preventDefault();
        e.stopPropagation();

        e.currentTarget.classList.add('was-validated');
        if (e.currentTarget.checkValidity() && validate(model)) {
            e.currentTarget.classList.remove('was-validated');
            props.onSubmit(e, model);
        }
    }

    const validate = (model: SearchPhysicianModel) => {
        var isValid = true;
        ValidatorHelper.clearCustomValidation(ssn4Ref.current);

        if (model.ssn4.length !== 4) {
            ValidatorHelper.setCustomValidationWithFeedback(ssn4Ref.current, "You must provide exactly 4 digits.");
            isValid = false;
        }

        return isValid;
    }

    if (!siteKey) return <AbpnSpinner />
    return (
        <form onSubmit={(e) => onSubmit(e, model)}>
            <Row>
                <Col xs={12} md={6} lg={6} xl={6}>
                    <Form.Group controlId="LastName" className="mt-4">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">
                            Last Name<span className="text-pp-primary ml-2">*</span>
                        </Form.Label>
                        <Form.Control type="input" required
                            onChange={e => updateLastName(e.target.value)} />
                    </Form.Group>
                    <Form.Group controlId="DateOfBirth" className="mt-4">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">
                            Date of Birth<span className="text-pp-primary ml-2">*</span>
                        </Form.Label>
                        <InputMask mask="99/99/9999"
                            onChange={e => updateDateOfBirth(e.target.value)} >
                            {(inputProps: any) => {
                                return <Form.Control {...inputProps} placeholder="MM/DD/YYYY" required type="input" />
                            }}
                        </InputMask>
                    </Form.Group>
                    <Form.Group controlId="SSN4" className="mt-4">
                        <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">
                            Last Four of SSN<span className="text-pp-primary ml-2">*</span>
                            <OverlayTrigger
                                placement={"right"}
                                overlay={<Tooltip id="ssn4" bsPrefix="pp-tooltip">
                                    If you are from Canada, please enter last 4 digits of your SIN
                                </Tooltip>}>
                                <i className="las la-info-circle ml-1"></i>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="input" ref={ssn4Ref} required
                            onChange={e => updateLastFourOfSSN(e.target.value)} />
                        <span className="invalid-feedback"></span>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-4">
                <Col>
                    <ReCAPTCHA sitekey={siteKey} onChange={(response: any) => handleCaptcha(response)} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button type="submit" disabled={!captchaResponse} variant="pp-primary">Submit</Button>
                    <Link to="/">
                        <Button variant="outline-pp-primary" className="ml-4">Cancel</Button>
                    </Link>
                </Col>
            </Row>
        </form>
    );
}

export default SearchPhysician;